import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * TableBadge Component
 * A customizable badge component for displaying labels in tables
 * 
 * @param {Object} props
 * @param {string} props.label - The text to display in the badge
 * @param {string} props.color - Text color (hex, rgb, or theme color)
 * @param {string} props.background - Background color (hex, rgb, or theme color)
 * @param {string} props.border - Border color (hex, rgb, or theme color)
 * @param {string} props.borderWidth - Border width (default: '1px')
 * @param {string} props.borderStyle - Border style (default: 'solid')
 * @param {string} props.size - Badge size ('small', 'medium', 'large', default: 'small')
 * @param {string} props.variant - Badge variant ('filled', 'outlined', default: 'filled')
 * @param {boolean} props.uppercase - Whether to make text uppercase (default: false)
 * @param {Object} props.sx - Additional custom styles
 * @param {string} props.className - Additional CSS classes
 */
const TableBadge = ({
  label,
  color = '#616161',
  background = '#f5f5f5',
  border = '#e0e0e0',
  borderWidth = '1px',
  borderStyle = 'solid',
  size = 'small',
  variant = 'filled',
  uppercase = false,
  sx = {},
  className = '',
  ...otherProps
}) => {
  // Size configurations
  const sizeConfigs = {
    small: {
      padding: '2px 8px',
      fontSize: '0.7rem',
      height: '20px',
      borderRadius: '12px'
    },
    medium: {
      padding: '4px 12px',
      fontSize: '0.75rem',
      height: '24px',
      borderRadius: '14px'
    },
    large: {
      padding: '6px 16px',
      fontSize: '0.8rem',
      height: '28px',
      borderRadius: '16px'
    }
  };

  // Variant configurations
  const variantConfigs = {
    filled: {
      backgroundColor: background,
      color: color,
      border: `${borderWidth} ${borderStyle} ${border}`
    },
    outlined: {
      backgroundColor: 'transparent',
      color: color,
      border: `${borderWidth} ${borderStyle} ${border}`
    }
  };

  const currentSize = sizeConfigs[size] || sizeConfigs.small;
  const currentVariant = variantConfigs[variant] || variantConfigs.filled;

  return (
    <Box
      className={`table-badge ${className}`}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 'fit-content',
        minWidth: 'fit-content',
        fontWeight: 600,
        textTransform: uppercase ? 'uppercase' : 'none',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        transition: 'all 0.2s ease-in-out',
        cursor: 'default',
        userSelect: 'none',
        ...currentSize,
        ...currentVariant,
        ...sx
      }}
      {...otherProps}
    >
      <Typography
        variant="caption"
        sx={{
          fontSize: 'inherit',
          fontWeight: 'inherit',
          color: 'inherit',
          textAlign: 'center',
          lineHeight: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

export default TableBadge; 